import { findFirstNonEmptyString } from "@product/scmp-sdk";
import first from "lodash/first";
import { forwardRef } from "react";
import { graphql, useFragment } from "react-relay";

import { ArticleSpeechSkipContainer } from "~/components/article/article-speech";
import { useResponsiveImage } from "~/components/common/base-image/hooks";
import type { articleLandingBannerArticle$key } from "~/queries/__generated__/articleLandingBannerArticle.graphql";

import type { ContainerProps } from "./styles";
import {
  BackgroundImageContainer,
  Container,
  Content,
  Headline,
  StyledArticleShareWidget,
  StyledBaseImage,
  StyledEntityLink,
  Wrapper,
} from "./styles";

type Props = {
  className?: string;
  reference: articleLandingBannerArticle$key;
  variant: ContainerProps["$variant"];
};

export const ArticleLandingBanner = forwardRef<HTMLDivElement, Props>(
  ({ className, reference: reference_, variant }, reference) => {
    const article = useFragment(
      graphql`
        fragment articleLandingBannerArticle on Article {
          ...articleSponsorContent
          ...articleShareWidgetArticle
          headline
          topics {
            ...entityLink
            name
          }
          images {
            isSlideshow
            title
            url
            size768x768: style(filter: { style: "768x768" }) {
              url
            }
            size800x1200: style(filter: { style: "800x1200" }) {
              url
            }
            sizeLandscape250x99: style(filter: { style: "landscape_250_99" }) {
              url
            }
          }
          coverDesktop: image(filter: { type: COVER }) {
            url
            sizeLandscape250x99: style(filter: { style: "landscape_250_99" }) {
              url
            }
          }
          coverMobile: image(filter: { type: COVER_MOBILE }) {
            url
          }
        }
      `,
      reference_,
    );

    const slideShowImage = first(article.images?.filter(image => image?.isSlideshow));
    const defaultImageUrl =
      "data:image/gif;base64,iVBORw0KGgoAAAANSUhEUgAAAAMAAAACCAQAAAA3fa6RAAAAEElEQVR42mN8+p8BCBghFAAiEQPLD3901QAAAABJRU5ErkJggg==";

    const variantSpecs = {
      desktopUp: {
        aspectRatio: "3/2",
        url: findFirstNonEmptyString(
          article.coverDesktop?.sizeLandscape250x99?.url,
          slideShowImage?.sizeLandscape250x99?.url,
          defaultImageUrl,
        ),
        width: 1200,
      },
      mobileUp: {
        aspectRatio: "1/1",
        url: findFirstNonEmptyString(
          article.coverMobile?.url,
          slideShowImage?.size800x1200?.url,
          defaultImageUrl,
        ),
        width: 425,
      },
      tabletUp: {
        aspectRatio: "1/1",
        url: findFirstNonEmptyString(
          article.coverDesktop?.url,
          slideShowImage?.size768x768?.url,
          defaultImageUrl,
        ),
        width: 768,
      },
    };

    const mapVariant = (viewportSize: "desktopUp" | "tabletUp" | "mobileUp") => ({
      aspectRatio: variantSpecs[viewportSize].aspectRatio,
      url: variantSpecs[viewportSize].url,
      width: variantSpecs[viewportSize].width,
    });

    const responsiveVariant = {
      desktopUp: mapVariant("desktopUp"),
      mobileUp: mapVariant("mobileUp"),
      tabletUp: mapVariant("tabletUp"),
    };

    const { bindResponsiveAspectRatio } = useResponsiveImage(responsiveVariant);

    const mainTopic = first(article?.topics);
    const coverImage = first(article.images);
    return (
      <Container $variant={variant} className={className} ref={reference}>
        <ArticleSpeechSkipContainer>
          <BackgroundImageContainer>
            <StyledBaseImage
              {...bindResponsiveAspectRatio}
              $objectFit="cover"
              responsiveVariant={responsiveVariant}
              src={coverImage?.url}
              title={coverImage?.title ?? ""}
            />
          </BackgroundImageContainer>
        </ArticleSpeechSkipContainer>

        <Content>
          <Wrapper>
            <Headline>{article.headline}</Headline>
            {mainTopic && (
              <StyledEntityLink reference={mainTopic}>{mainTopic.name}</StyledEntityLink>
            )}
            <StyledArticleShareWidget reference={article} variant="landingBanner" />
          </Wrapper>
        </Content>
      </Container>
    );
  },
);

ArticleLandingBanner.displayName = "ArticleLandingBanner";
